// Dev-only helpers for exercising the account-level FTUX gate locally.
//
// Real accounts already have ftux_complete === true on the server, so the
// first-time onboarding flow is otherwise impossible to reach in local dev.
// These helpers let a developer force the gate's ftux_complete state and
// simulate a failing set_post_ftux call straight from the browser console,
// without a rebuild.
//
// Everything here is INERT unless `import.meta.env.DEV` is true AND an override
// has been explicitly set. `import.meta.env.DEV` is true ONLY under a local
// `pnpm dev` (vite serve); it is statically `false` for ANY `vite build`, so
// these overrides do nothing on the Firebase deploy-preview link or on
// staging/UAT/production — every guarded branch below is dead-code eliminated
// (tree-shaken) and the localStorage reads never run there. Use them from a
// LOCAL `pnpm dev` browser console only.
//
// Usage (local `pnpm dev` browser console) — read gate:
//   localStorage.setItem('devFtuxCompleteOverride', 'null')   // brand-new profile → onboarding shows
//   localStorage.setItem('devFtuxCompleteOverride', 'false')  // → onboarding shows
//   localStorage.setItem('devFtuxCompleteOverride', 'true')   // → onboarding skipped
//   localStorage.removeItem('devFtuxCompleteOverride')        // → real server value (default, inert)
//
// Usage (local `pnpm dev` browser console) — set_post_ftux resilience:
//   localStorage.setItem('devFtuxSetFailureMode', 'transient') // first attempt fails, retry recovers
//   localStorage.setItem('devFtuxSetFailureMode', 'permanent') // every attempt fails → error surfaced
//   localStorage.removeItem('devFtuxSetFailureMode')           // → real behaviour (default, inert)

const FTUX_COMPLETE_OVERRIDE_KEY = 'devFtuxCompleteOverride';
const FTUX_SET_FAILURE_MODE_KEY = 'devFtuxSetFailureMode';

/**
 * Resolves the ftux_complete value the returning-member gate should use. In
 * production — or in dev with no override set — it returns the real server
 * value unchanged. The gate keeps its own `=== true` comparison, so a `null`
 * override maps to "onboarding shows", matching a brand-new profile.
 */
export const resolveFtuxComplete = (
  actual: boolean | null | undefined,
): boolean | null | undefined => {
  if (!import.meta.env.DEV) return actual;
  switch (window.localStorage.getItem(FTUX_COMPLETE_OVERRIDE_KEY)) {
    case 'true':
      return true;
    case 'false':
      return false;
    case 'null':
      return null;
    default:
      return actual;
  }
};

/**
 * Simulates a set_post_ftux failure for the given 1-based attempt when a dev
 * failure mode is set. `permanent` throws on every attempt (so the error
 * surfaces after retries are exhausted); `transient` throws only on the first
 * (so the retry recovers). No-op in production or when unset, leaving the real
 * socket call to run.
 */
export const simulateDevFtuxSetFailure = (attempt: number): void => {
  if (!import.meta.env.DEV) return;
  const mode = window.localStorage.getItem(FTUX_SET_FAILURE_MODE_KEY);
  if (mode === 'permanent' || (mode === 'transient' && attempt === 1)) {
    throw new Error(`[dev] simulated ${mode} set_post_ftux failure`);
  }
};
